import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Thumbnail } from '../Thumbnail';
import { useDynamicMaxHeight } from '../../hooks/useDynamicMaxHeight';

const ThumbnailList = ({
  thumbnails,
  onThumbnailClick,
  onThumbnailDoubleClick,
  onClickUntrack,
  activeDisplaySetInstanceUIDs = [],
  viewPreset,
  ThumbnailMenuItems,
}) => {
  const { ref, maxHeight } = useDynamicMaxHeight(thumbnails);
  const [isMobile, setIsMobile] = useState(false);
  const hasAutoSwitchedRef = useRef(false);
  const containerRef = useRef(null);
  const scrollContentRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      const isMobileDevice = /(Mobi|Android|iPhone|iPad|iPod)/.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Efecto para forzar estilos y manejar la barra de desplazamiento
  useEffect(() => {
    if (containerRef.current && scrollContentRef.current) {
      const container = containerRef.current;
      const scrollContent = scrollContentRef.current;

      // Estilos base del contenedor
      container.style.setProperty('max-height', '100%', 'important');
      container.style.setProperty('display', 'flex', 'important');
      container.style.setProperty('flex-direction', 'row', 'important');
      container.style.setProperty('position', 'relative', 'important');

      // Configuración de la barra de desplazamiento
      container.style.setProperty('overflow-x', 'auto', 'important');
      container.style.setProperty('overflow-y', 'hidden', 'important');
      container.style.setProperty('scrollbar-width', 'thin', 'important');
      container.style.setProperty(
        'scrollbar-color',
        'rgba(255,255,255,0.5) transparent',
        'important'
      );

      // Estilos para webkit (Chrome, Safari)
      container.style.setProperty('--scrollbar-thumb', 'rgba(255,255,255,0.5)', 'important');
      container.style.setProperty('--scrollbar-track', 'transparent', 'important');

      // Estilos específicos para móvil/desktop
      if (!isMobile) {
        container.style.setProperty('justify-content', 'center', 'important');
        container.style.setProperty('transform', 'rotate(180deg)', 'important');
        container.style.setProperty('direction', 'ltr', 'important');
        container.style.setProperty('padding', '0.5rem 0', 'important');
      } else {
        container.style.setProperty('padding-top', '1.25rem', 'important');
        container.style.setProperty('padding-bottom', '1.25rem', 'important');
      }

      // Estilos del contenido desplazable
      scrollContent.style.setProperty('display', 'flex', 'important');
      scrollContent.style.setProperty('flex-direction', 'row', 'important');
      scrollContent.style.setProperty('align-items', 'center', 'important');

      if (!isMobile) {
        scrollContent.style.setProperty('transform', 'rotate(180deg)', 'important');
      }

      // Añadir estilos para la barra de desplazamiento personalizada
      const styleElement = document.createElement('style');
      styleElement.textContent = `
        #ohif-thumbnail-list::-webkit-scrollbar {
          height: 6px !important;
          background-color: transparent !important;
        }
        #ohif-thumbnail-list::-webkit-scrollbar-thumb {
          background-color: var(--scrollbar-thumb) !important;
          border-radius: 3px !important;
        }
        #ohif-thumbnail-list::-webkit-scrollbar-track {
          background-color: var(--scrollbar-track) !important;
        }
      `;
      document.head.appendChild(styleElement);

      return () => {
        document.head.removeChild(styleElement);
      };
    }
  }, [isMobile]);

  // Resto del código del componente...
  useEffect(() => {
    if (!thumbnails || thumbnails.length === 0 || hasAutoSwitchedRef.current) {
      return;
    }

    const activeNoImage = thumbnails.find(
      thumb =>
        activeDisplaySetInstanceUIDs.includes(thumb.displaySetInstanceUID) &&
        thumb.componentType === 'thumbnailNoImage'
    );

    const hasOtherActive = thumbnails.some(
      thumb =>
        activeDisplaySetInstanceUIDs.includes(thumb.displaySetInstanceUID) &&
        thumb.componentType !== 'thumbnailNoImage'
    );

    if (activeNoImage && !hasOtherActive) {
      const firstWithImage = thumbnails.find(
        thumb =>
          (thumb.componentType === 'thumbnail' || thumb.componentType === 'thumbnailTracked') &&
          thumb.displaySetInstanceUID !== activeNoImage.displaySetInstanceUID
      );

      if (firstWithImage) {
        hasAutoSwitchedRef.current = true;
        onThumbnailClick(firstWithImage.displaySetInstanceUID);
      }
    }
  }, [thumbnails, activeDisplaySetInstanceUIDs, onThumbnailClick]);

  const listItems =
    thumbnails?.filter(
      ({ componentType, description }) =>
        componentType === 'thumbnailNoImage' && description === 'Report PDF'
    ) ?? [];

  const thumbnailItems = thumbnails?.filter(
    ({ componentType }) => componentType !== 'thumbnailNoImage' && viewPreset === 'thumbnails'
  );

  const sortedThumbnails = [...thumbnailItems, ...listItems];

  return (
    <div
      id="ohif-thumbnail-list"
      ref={containerRef}
      className="" // Eliminamos todas las clases
    >
      <div
        ref={scrollContentRef}
        className="" // Eliminamos todas las clases
      >
        {sortedThumbnails.map(item => {
          const { displaySetInstanceUID, componentType, numInstances, ...rest } = item;
          const isActive = activeDisplaySetInstanceUIDs.includes(displaySetInstanceUID);
          const preset = componentType === 'thumbnailNoImage' ? 'list' : 'thumbnails';

          return (
            <Thumbnail
              key={displaySetInstanceUID}
              {...rest}
              displaySetInstanceUID={displaySetInstanceUID}
              numInstances={numInstances || 1}
              isActive={isActive}
              thumbnailType={componentType}
              viewPreset={preset}
              onClick={onThumbnailClick.bind(null, displaySetInstanceUID)}
              onDoubleClick={onThumbnailDoubleClick.bind(null, displaySetInstanceUID)}
              onClickUntrack={onClickUntrack.bind(null, displaySetInstanceUID)}
              ThumbnailMenuItems={ThumbnailMenuItems}
            />
          );
        })}
      </div>
    </div>
  );
};

ThumbnailList.propTypes = {
  thumbnails: PropTypes.arrayOf(
    PropTypes.shape({
      displaySetInstanceUID: PropTypes.string.isRequired,
      imageSrc: PropTypes.string,
      imageAltText: PropTypes.string,
      seriesDate: PropTypes.string,
      seriesNumber: PropTypes.any,
      numInstances: PropTypes.number,
      description: PropTypes.string,
      componentType: PropTypes.any,
      isTracked: PropTypes.bool,
      dragData: PropTypes.shape({
        type: PropTypes.string.isRequired,
      }),
    })
  ),
  activeDisplaySetInstanceUIDs: PropTypes.arrayOf(PropTypes.string),
  onThumbnailClick: PropTypes.func.isRequired,
  onThumbnailDoubleClick: PropTypes.func.isRequired,
  onClickUntrack: PropTypes.func.isRequired,
  viewPreset: PropTypes.string,
  ThumbnailMenuItems: PropTypes.any,
};

export { ThumbnailList };
